import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Gestión de importaciones";
const heroImage = "https://net-basis.net/Bilder/import-management-customs-clearance-dach-distribution.webp";
const heroAlt = "Contenedores apilados simbolizan gestión de importaciones, despacho aduanero y ventas locales.";
const heroClaimLine1 = "¡Mucho más que";
const heroClaimLine2 = "simple transporte!";
const benefitsTitle = "Cómo se beneficia de nuestra experiencia en __gestión de importaciones__:";
const introText = "Gestión de importaciones eficiente para su acceso sin fisuras al mercado de la región DACH. Como expertos locales, actuamos para los fabricantes extranjeros de TIC como un departamento de importación local plenamente integrado y un sólido vínculo comercial con sus mercados objetivo.";
const p1Text = "NetBasis International sirve a sus fabricantes socios extranjeros sobre el terreno como su departamento de importación local. Nos encargamos de todas las actividades esenciales de importación, así como de las labores locales de ventas y marketing dentro del espacio económico de habla alemana, garantizando que nuestros socios se beneficien directamente de nuestra red local y de un acceso inmediato a los socios comerciales adecuados.";
const p1Parts = p1Text.split(COMPANY_NAME);
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Asunción completa de todo el proceso de importación, incluyendo el despacho de aduanas profesional",
  "Clasificación correcta de los productos según los códigos arancelarios y un intercambio fluido de información con todos los socios comerciales",
  "Garantía fiable del etiquetado y embalaje correctos de los productos de acuerdo con las normativas nacionales específicas",
  "Ejecución legal de todos los procesos con estricto cumplimiento de las prohibiciones y restricciones locales",
  "Cobertura integral y minimización de todos los riesgos financieros y jurídicos sobre el terreno",
  "Logística impecable y entrega puntual a los distribuidores y comerciantes locales",
  "Ejecución estratégica de publicidad dirigida, marketing y acciones promocionales de ventas",
  "Planificación, organización y realización profesional de ferias comerciales y congresos pertinentes, así como la participación en ellos",
  "Asunción y gestión fiables de toda la facturación y contabilidad financiera local",
];

export default function GestionDeImportacionesEsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Gestión de Importaciones | NetBasis International"
        description="Tramitación fluida, coordinación logística y cumplimiento de todas las normativas locales para sus flujos internacionales de mercancías."
        ogUrl="https://net-basis.net/es/servicios/desarrollo-de-negocio/gestion-de-importaciones"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start overflow-hidden pointer-events-none pt-10 sm:pt-16 md:pt-[58px] lg:pt-[72px] pl-8 sm:pl-8 md:pl-[62px] lg:pl-14">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left importmanagement-hero-text-slide im-hero-lh-15"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block md:inline">{heroClaimLine1}</span>
                  <span className="hidden md:inline">&nbsp;</span>
                  <span className="block md:inline">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 leading-relaxed text-sm md:text-base mb-4">
              {introText}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i} className="mr-[0.15em]">{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}